// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/tool/ToolListPage.jsx
// 📝 Purpose:
//   Tool Master – List Page
//   - Gradient header
//   - KPI summary bar
//   - Search + status filter
//   - Table view
//   - Create / Edit Drawer
// ======================================================================

import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";

import axiosClient from "../../../../../api/axiosClient";
import { AuthContext } from "../../../../../context/AuthContext";

import ToolDrawer from "./ToolDrawer";
import ToolKpiBar from "../../../components/tool/ToolKpiBar";

// ----------------------------------------------------------------------
export default function ToolListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  const [toolTypes, setToolTypes] = useState([]);

  // ------------------------------------------------------------
  // Load tools
  // ------------------------------------------------------------
  const loadTools = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/tools"
      );
      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load tools", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ------------------------------------------------------------
  // Load Tool Types (lookup)
  // ------------------------------------------------------------
  const loadToolTypes = useCallback(async () => {
  try {
    const res = await axiosClient.get(
      "/api/ppe/masters/lookups/tool-types"
    );
    setToolTypes(res.data?.data || []);
  } catch (err) {
    console.error("Failed to load tool types", err);
    setToolTypes([]);
  }
}, []);
  useEffect(() => {
    loadTools();
    loadToolTypes();
  }, [loadTools, loadToolTypes]);

  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // Filtering
  // ------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.tool_code?.toLowerCase().includes(q) ||
      r.tool_name?.toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" ||
      (status === "ACTIVE" && r.is_active) ||
      (status === "INACTIVE" && !r.is_active);

    return matchesSearch && matchesStatus;
  });

  const toolTypeMap = React.useMemo(() => {
    const map = {};
    toolTypes.forEach((t) => {
      map[t.id] = t.lookup_name;
    });
    return map;
  }, [toolTypes]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Tools</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Manage production tools and fixtures
          </div>
        </div>

        {permissions?.TOOL_CREATE?.create && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create Tool
          </button>
        )}
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <ToolKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadTools}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 140 }}>Tool Code</th>
                <th>Tool Name</th>
                <th style={{ width: 160 }}>Tool Type</th>
                <th style={{ width: 120 }}>Shared</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center text-muted"
                  >
                    No tools found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.tool_code}</td>
                  <td>{r.tool_name}</td>
                  <td>{toolTypeMap[r.tool_type_id] || "-"}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_shared
                          ? "bg-primary"
                          : "bg-secondary"
                      }`}
                    >
                      {r.is_shared ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_active
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    {permissions?.TOOL_EDIT?.edit && (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openEditDrawer(r)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* Drawer */}
      {/* ====================================================== */}
      <ToolDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        toolTypes={toolTypes}
        onClose={closeDrawer}
        onSaved={loadTools}
      />
    </div>
  );
}
